import { useTranslations } from "@acto/web-common/locales";
import { useDirectionalNavigate } from "@acto/taskpane/field-sales-2/utils/viewTransitions";
import { NudgeBanner } from "@acto/taskpane/field-sales-2/components/patterns/NudgeBanner";
import { useTodos } from "@acto/taskpane/core/data-access/todos/useTodos";
import { useContributeChatScope } from "@acto/taskpane/core/chat-envelope";
import ActoRoutes from "@acto/taskpane/core/router/routes.config";
import { OverdueBellIcon } from "./OverdueBellIcon";

interface OverdueTodosBannerProps {
  className?: string;
}

/**
 * Home nudge pill for overdue to-dos. Deep-links into the Tasks tab with the "overdue" filter
 * pre-applied. There is no cheap global overdue count (the `todos` connection has no
 * `totalCount`), so the pill shows a static label and only its presence is data-driven: it uses
 * the same `showOverdue` filter as the Tasks overdue bucket and hides itself when that is empty.
 */
export function OverdueTodosBanner({ className }: OverdueTodosBannerProps) {
  const t = useTranslations("fieldSalesTwo.todosList");
  const navigate = useDirectionalNavigate();
  // limit 1: we only need existence, not a count.
  const { data, isFetching } = useTodos({ showOverdue: true, limit: 1 });

  const hasOverdue = (data?.length ?? 0) > 0;

  // Ambient context: presence only — the todos connection has no cheap global count.
  const overdueFragment = { has_overdue_todos: data ? data.length > 0 : undefined };
  useContributeChatScope("home", overdueFragment);

  if (isFetching || !hasOverdue) {
    return null;
  }

  const handleNavigateToOverdue = () => {
    navigate(ActoRoutes.TODOS_OVERVIEW, {
      direction: "push",
      state: { initialFilter: "overdue" },
    });
  };

  return (
    <NudgeBanner
      className={className}
      icon={<OverdueBellIcon />}
      label={t("entryBanner")}
      onClick={handleNavigateToOverdue}
    />
  );
}
